import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { modelsData, showcaseCategories } from '../data/car360Data';
import Car360Viewer from './Car360Viewer';
import { motion, AnimatePresence } from 'framer-motion';

const ModelShowcase = () => {
    const { language } = useLanguage();
    const t = translations[language].modelShowcase;

    const [activeCategory, setActiveCategory] = useState(showcaseCategories[0]);
    const [activeModelId, setActiveModelId] = useState(showcaseCategories[0].models[0]);

    const getCategoryLabel = (id) => {
        if (id === 'off-road') return t.categories.offRoad;
        if (id === 'sedan') return t.categories.sedan;
        return id;
    };

    const getModelName = (id) => {
        if (id === 'bj30') return t.models.bj30;
        if (id === 'bj40-honor') return t.models.bj40Honor;
        if (id === 'bj40-pro') return t.models.bj40Pro;
        if (id === 'u5-plus') return t.models.u5Plus;
        return modelsData[id]?.name;
    };

    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        // Select first model of the new category, if available
        if (category.models.length > 0) {
            setActiveModelId(category.models[0]);
        } else {
            setActiveModelId(null);
        }
    };

    const activeModelData = activeModelId ? modelsData[activeModelId] : null;

    return (
        <section className="py-20 bg-white">
            <div className="container mx-auto px-4">

                {/* Category Tabs */}
                <div className="flex justify-start md:justify-center mb-12 overflow-x-auto pb-4 scrollbar-hide no-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
                    <div className="flex gap-8 md:gap-12 border-b border-gray-200 min-w-max">
                        {showcaseCategories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => handleCategoryChange(category)}
                                className={`pb-4 text-base md:text-xl font-medium transition-all duration-300 relative whitespace-nowrap ${activeCategory.id === category.id
                                    ? 'text-black'
                                    : 'text-gray-400 hover:text-gray-600'
                                    }`}
                            >
                                {getCategoryLabel(category.id)}
                                {/* Active Indicator */}
                                <span className={`absolute bottom-0 left-0 w-full h-[3px] bg-red-600 transform transition-transform duration-300 ${activeCategory.id === category.id ? 'scale-x-100' : 'scale-x-0'
                                    }`} />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Dynamic Content Area */}
                <AnimatePresence mode="wait">
                    {activeModelData ? (
                        <motion.div
                            key={activeModelId}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5 }}
                            className="animate-fade-in-up"
                        >
                            {/* Model Title */}
                            <div className="text-center mb-8">
                                <h2 className="text-3xl md:text-5xl font-bold text-gray-800 tracking-tight">
                                    {getModelName(activeModelId)}
                                </h2>
                            </div>

                            {/* Sub-navigation (if multiple models in category) */}
                            {activeCategory.models.length > 1 && (
                                <div className="flex justify-center gap-4 mb-8">
                                    {activeCategory.models.map(modelId => {
                                        const model = modelsData[modelId];
                                        if (!model) return null;
                                        return (
                                            <button
                                                key={modelId}
                                                onClick={() => setActiveModelId(modelId)}
                                                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${activeModelId === modelId
                                                    ? 'bg-black text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {getModelName(modelId)}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}

                            {/* 360 Viewer Component */}
                            <div className="w-full">
                                <Car360Viewer modelName={activeModelId} />
                            </div>

                        </motion.div>
                    ) : (
                        <motion.div
                            key="empty"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-center py-20 text-gray-400"
                        >
                            <p>No models available in this category yet.</p>
                        </motion.div>
                    )}
                </AnimatePresence>

            </div>
        </section>
    );
};

export default ModelShowcase;
